/**
 * returnTo — preserve the user's in-app location + search across an
 * involuntary re-login (HIPAA idle-timeout or cross-tab session expiry).
 *
 * Ashley's problem: after 30 minutes idle the HIPAA session timeout
 * hard-redirects her to /login, and her deposit search (quick-search term
 * + filters + page) is gone. We snapshot the pre-logout URL on the
 * involuntary redirect and replay it after Google OAuth completes, so she
 * lands back on the exact same filtered/searched view.
 *
 * HIPAA note: the deposit quick-search term can contain payer/patient text,
 * so — like the auth token — this snapshot lives in sessionStorage (clears
 * on tab close, matching the app's "no sensitive data persisted" posture),
 * NOT localStorage. sessionStorage survives the same-tab OAuth redirect,
 * which is exactly Ashley's flow. Neither logout() nor forceLogout() call
 * sessionStorage.clear() (they remove only specific keys), so this snapshot
 * survives the logout that immediately precedes the redirect.
 */

// Greppable marker — a plain string literal (safe from JS minification/mangling)
// so a deploy can verify this feature shipped by grepping the served bundle.
export const RETURN_TO_KEY = 'agi.returnTo';

// Only these protected app routes are worth restoring to. Never restore to
// /login, /auth/callback, /, /welcome, or /privacy.
const ALLOWED_PATHS = ['/deposits', '/entry-periods'];

// Snapshots older than this are treated as stale intent and ignored.
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

interface ReturnToSnapshot {
  url: string;
  ts: number;
}

const isAllowedPath = (pathname: string): boolean => ALLOWED_PATHS.includes(pathname);

/**
 * Snapshot the current in-app location (pathname + query string) so it can be
 * restored after an involuntary re-login. No-op on non-app / auth routes so a
 * voluntary logout or a redirect that is already on /login never overwrites a
 * legitimate saved view. Best-effort — storage failures are swallowed.
 */
export const saveReturnTo = (): void => {
  try {
    const { pathname, search } = window.location;
    if (!isAllowedPath(pathname)) return;

    const snapshot: ReturnToSnapshot = {
      url: pathname + search,
      ts: Date.now(),
    };
    sessionStorage.setItem(RETURN_TO_KEY, JSON.stringify(snapshot));
  } catch {
    // Storage unavailable / quota exceeded — restoring the search is best-effort.
  }
};

/**
 * Read and clear the saved return-to URL (one-time use). Returns the URL only
 * when it is fresh (< 24h), app-relative, and points at an allowed app route;
 * otherwise null. The key is always removed so a stale snapshot can't leak into
 * a later, unrelated login.
 */
export const consumeReturnTo = (): string | null => {
  try {
    const raw = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    if (!raw) return null;

    const snapshot = JSON.parse(raw) as Partial<ReturnToSnapshot>;
    const { url, ts } = snapshot;

    if (typeof url !== 'string' || typeof ts !== 'number') return null;
    if (Date.now() - ts > MAX_AGE_MS) return null;
    // Must be an app-relative path; reject protocol-relative "//evil.com" too.
    if (!url.startsWith('/') || url.startsWith('//')) return null;

    const pathname = url.split('?')[0].split('#')[0];
    if (!isAllowedPath(pathname)) return null;

    return url;
  } catch {
    return null;
  }
};
